import { BadgeEstadoColada, FechaCR, FiltroPlantaMulti, PickerObras } from '@/components/dominio';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multi-select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { useAnularColadasBulk, useColadas, useConsolidarColadas } from '@/hooks/useColadas';
import { ApiError, cliente, esAdmin, esSoloOperadorFiori, puedeOperarColadas } from '@/lib/cliente';
import { nombrePlantaDisplay } from '@/lib/plantas';
import { cn } from '@/lib/utils';
import type { ColadaListadoItem, EstadoColada } from '@adelante/shared';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Ban,
  Building2,
  Check,
  ChevronLeft,
  ChevronRight,
  Combine,
  Search,
  X,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * `/coladas` — listado con filtros y paginación.
 *
 *   - Filtros: estado (multi), planta, fechas, búsqueda libre (q).
 *   - Paginación: 25 por página default.
 *   - Click en fila → navega a `/coladas/:id`.
 */

const ESTADOS: EstadoColada[] = ['sugerida', 'confirmada', 'digitada', 'cerrada', 'anulada'];
const POR_PAGINA = 25;

// IDs estáticos para los skeletons (5 filas × 13 columnas: checkbox +
// las 12 originales). Usamos strings para que biome no marque uso de
// índice numérico como key.
const SKELETON_FILAS = ['fila-a', 'fila-b', 'fila-c', 'fila-d', 'fila-e'] as const;
const SKELETON_COLUMNAS = [
  'col-0',
  'col-1',
  'col-2',
  'col-3',
  'col-4',
  'col-5',
  'col-6',
  'col-7',
  'col-8',
  'col-9',
  'col-10',
  'col-11',
  'col-12',
] as const;

type PedidoBcEstadoFiltro = 'con' | 'sin';
type OrdenColadas = 'fecha_desc' | 'pedido_bc_asc' | 'pedido_bc_desc';

// Conjuntos válidos para sanear lo que vengan en la URL.
const PEDIDO_BC_FILTRO_VALORES: readonly PedidoBcEstadoFiltro[] = ['con', 'sin'];
const ORDEN_VALORES: readonly OrdenColadas[] = ['fecha_desc', 'pedido_bc_asc', 'pedido_bc_desc'];

// Lee un param CSV de la URL (`?estado=confirmada,digitada`) y filtra
// contra una whitelist — valores desconocidos se descartan en silencio.
function leerListaCsv<T extends string>(
  searchParams: URLSearchParams,
  clave: string,
  valida: readonly T[],
): T[] {
  const raw = searchParams.get(clave);
  if (!raw) return [];
  return raw
    .split(',')
    .map((v) => v.trim())
    .filter((v): v is T => (valida as readonly string[]).includes(v));
}

export function ColadasListaPantalla() {
  const navegar = useNavigate();
  const location = useLocation();
  // Operador Fiori "puro": el backend ya acota este listado a lo que él
  // mismo registró. Los filtros de N° Pedido BC (envío a BC, no lo tocan)
  // no le sirven de nada — los ocultamos para que la pantalla sea usable
  // en celular sin scrollear entre filtros que no va a usar.
  const soloOperadorFiori = esSoloOperadorFiori();

  // Filtros y paginación se mantienen en URL search params (no useState).
  // Beneficios:
  //   - Al volver del detalle (browser back o "Volver al listado"), la URL
  //     preserva los filtros y la página → el componente los re-lee.
  //   - Links compartibles: /coladas?estado=cerrada&desde=2026-01-01.
  //   - Sobrevive a F5 / recarga manual.
  // Cambios usan `replace: true` para no saturar el history del browser.
  const [searchParams, setSearchParams] = useSearchParams();

  // IMPORTANTE: aceptamos múltiples cambios en una sola llamada. Si haces
  // dos `setSearchParams` seguidos (uno para el filtro, otro para resetear
  // página), AMBAS funciones updater reciben el MISMO `prev` (el snapshot
  // del render actual), así que la segunda pisa a la primera y se pierde
  // el cambio del filtro. Por eso aplicamos todos los cambios juntos.
  const setParams = useCallback(
    (changes: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(changes)) {
            if (v === null || v === '') next.delete(k);
            else next.set(k, v);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Lectura saneada (si la URL viene con un valor inválido, caemos al default).
  // Estado/Planta/N° Pedido BC son multi-select — se guardan en la URL como
  // CSV (`?estado=confirmada,digitada`) para que el link siga siendo
  // compartible/persista a F5, igual que los filtros de un solo valor.
  const estadosFiltro = leerListaCsv(searchParams, 'estado', ESTADOS);

  const idsPlantaRaw = searchParams.get('id_planta');
  const idsPlanta: number[] = idsPlantaRaw
    ? idsPlantaRaw
        .split(',')
        .map((v) => v.trim())
        .filter((v) => /^\d+$/.test(v))
        .map(Number)
    : [];

  const desde = searchParams.get('desde') ?? '';
  const hasta = searchParams.get('hasta') ?? '';
  const q = searchParams.get('q') ?? '';
  const recetaBlendQ = searchParams.get('receta_blend_q') ?? '';

  const pedidoBcEstados = leerListaCsv(searchParams, 'pedido_bc_estado', PEDIDO_BC_FILTRO_VALORES);

  const pedidoBcQ = searchParams.get('pedido_bc_q') ?? '';

  const ordenRaw = searchParams.get('orden') ?? 'fecha_desc';
  const orden: OrdenColadas = ORDEN_VALORES.includes(ordenRaw as OrdenColadas)
    ? (ordenRaw as OrdenColadas)
    : 'fecha_desc';

  const paginaRaw = searchParams.get('pagina');
  const pagina = paginaRaw !== null && /^\d+$/.test(paginaRaw) ? Math.max(1, Number(paginaRaw)) : 1;

  // Setters que actualizan la URL. Cada uno (excepto setPagina) resetea
  // página a 1, igual que antes con resetPagina(). Todo en UN solo
  // setParams para que no se pisen los cambios.
  const setEstadosFiltro = (v: EstadoColada[]) => {
    setParams({ estado: v.length === 0 ? null : v.join(','), pagina: null });
  };
  const setIdsPlanta = (v: number[]) => {
    setParams({ id_planta: v.length === 0 ? null : v.join(','), pagina: null });
  };
  const setDesde = (v: string) => {
    setParams({ desde: v || null, pagina: null });
  };
  const setHasta = (v: string) => {
    setParams({ hasta: v || null, pagina: null });
  };
  const setQ = (v: string) => {
    setParams({ q: v || null, pagina: null });
  };
  const setRecetaBlendQ = (v: string) => {
    setParams({ receta_blend_q: v || null, pagina: null });
  };
  const setPedidoBcEstados = (v: PedidoBcEstadoFiltro[]) => {
    setParams({ pedido_bc_estado: v.length === 0 ? null : v.join(','), pagina: null });
    // Si dejamos de filtrar por "con" y quedó texto de búsqueda, no lo
    // limpiamos: `pedido_bc_q` ya se envía condicionalmente más abajo.
  };
  const setPedidoBcQ = (v: string) => {
    setParams({ pedido_bc_q: v || null, pagina: null });
  };
  const setOrden = (v: OrdenColadas) => {
    setParams({ orden: v === 'fecha_desc' ? null : v, pagina: null });
  };
  const setPagina = (v: number) => {
    setParams({ pagina: v <= 1 ? null : String(v) });
  };
  // Selección múltiple. Se permite seleccionar coladas en estados que
  // aceptan AL MENOS una acción bulk: sugerida (todas), confirmada y
  // digitada (asignar obra). Cada acción valida sus propias reglas más
  // estrictas (ej. consolidar solo entre sugeridas) en el botón.
  // El Set guarda id_colada.
  // Asignar obra en bulk y consolidar son operativos (Admin/Operador —
  // Ingeniería no, ver ROLES_OPERATIVOS del API).
  const puedeOperar = puedeOperarColadas();
  // Anular es Admin-only (mismo criterio que anular individual) — más
  // estricto que puedeOperar, que también incluye Operador.
  const puedeAnular = esAdmin();
  const [seleccionadas, setSeleccionadas] = useState<Set<number>>(new Set());
  const [modalConsolidar, setModalConsolidar] = useState(false);
  const [modalAsignarObraBulk, setModalAsignarObraBulk] = useState(false);
  const [modalAnularBulk, setModalAnularBulk] = useState(false);
  const [bulkObraEnCurso, setBulkObraEnCurso] = useState(false);
  const anularBulk = useAnularColadasBulk();
  // Error de consolidación que queremos mostrar en un modal persistente
  // (no en un toast efímero — los mensajes 409 ESTADO_INVALIDO son largos
  // y la operadora necesita leerlos con calma).
  const [errorConsolidacion, setErrorConsolidacion] = useState<{
    mensaje: string;
    codigo?: string;
    status?: number;
  } | null>(null);
  const consolidar = useConsolidarColadas();
  const qc = useQueryClient();

  /**
   * Asigna la misma obra (o la quita si es null) a TODAS las coladas
   * seleccionadas. Itera en serie, tolerando errores individuales para
   * no abortar el batch al primer fallo. Reporta resumen con UN solo toast.
   *
   * Invalida los caches relevantes al final (lista + detalles + huérfanos)
   * para que la UI refleje el nuevo works_no sin recargar.
   */
  const asignarObraEnBulk = async (obraWorksNo: string | null) => {
    const ids = Array.from(seleccionadas);
    if (ids.length === 0) return;

    setBulkObraEnCurso(true);
    let okCount = 0;
    const fallos: { id: number; mensaje: string }[] = [];

    for (const idColada of ids) {
      try {
        const r = await cliente.coladas.asignarObra(idColada, { obra_works_no: obraWorksNo });
        if (r.data) {
          okCount++;
        } else {
          fallos.push({ id: idColada, mensaje: r.error?.mensaje ?? 'Error desconocido' });
        }
      } catch (e) {
        fallos.push({
          id: idColada,
          mensaje: e instanceof Error ? e.message : 'Error de red',
        });
      }
    }

    setBulkObraEnCurso(false);
    setModalAsignarObraBulk(false);
    setSeleccionadas(new Set());

    qc.invalidateQueries({ queryKey: ['coladas'] });
    // El detalle de cada colada también puede estar cacheado en otra ruta.
    for (const id of ids) qc.invalidateQueries({ queryKey: ['colada', id] });

    if (fallos.length === 0) {
      toast.success(
        obraWorksNo
          ? `Obra ${obraWorksNo} asignada a ${okCount} ${okCount === 1 ? 'colada' : 'coladas'}`
          : `Obra removida de ${okCount} ${okCount === 1 ? 'colada' : 'coladas'}`,
      );
    } else if (okCount === 0) {
      toast.error(`Falló la asignación en ${fallos.length} coladas`, {
        description: fallos
          .slice(0, 3)
          .map((f) => `#${f.id}: ${f.mensaje}`)
          .join(' · '),
      });
    } else {
      toast.warning(`${okCount} OK, ${fallos.length} con error`, {
        description: fallos
          .slice(0, 3)
          .map((f) => `#${f.id}: ${f.mensaje}`)
          .join(' · '),
      });
    }
  };

  // Resetea la página al cambiar cualquier filtro.
  const filtros = useMemo(
    () => ({
      estado: estadosFiltro.length > 0 ? estadosFiltro : undefined,
      id_planta: idsPlanta.length > 0 ? idsPlanta : undefined,
      desde: desde || undefined,
      hasta: hasta || undefined,
      q: q.trim() || undefined,
      receta_blend_q: recetaBlendQ.trim() || undefined,
      pedido_bc_estado: pedidoBcEstados.length > 0 ? pedidoBcEstados : undefined,
      // Solo enviamos pedido_bc_q si no estamos filtrando SOLO por 'sin' (ahí no tiene sentido buscar texto).
      pedido_bc_q:
        !(pedidoBcEstados.length === 1 && pedidoBcEstados[0] === 'sin') && pedidoBcQ.trim()
          ? pedidoBcQ.trim()
          : undefined,
      orden: orden === 'fecha_desc' ? undefined : orden,
      pagina,
      por_pagina: POR_PAGINA,
    }),
    [
      estadosFiltro,
      idsPlanta,
      desde,
      hasta,
      q,
      recetaBlendQ,
      pedidoBcEstados,
      pedidoBcQ,
      orden,
      pagina,
    ],
  );

  const consulta = useColadas(filtros);
  const data = consulta.data?.data;
  const coladas = data?.coladas ?? [];
  const total = data?.total ?? 0;
  const totalPaginas = Math.max(1, Math.ceil(total / POR_PAGINA));

  // NOTA: no existe `resetPagina` helper. Cada setter (setEstadoFiltro,
  // setQ, etc.) ya resetea página a 1 internamente via setParams({...,
  // pagina: null}). Llamar resetPagina() después del setter creaba un
  // race entre dos `setSearchParams` que recibían el mismo `prev` snapshot,
  // y la segunda llamada pisaba a la primera (perdiendo el valor del filtro).

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Coladas</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Agrupaciones de batches que se digitan en Business Central como Pedidos de Ensamblado.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Filtros</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="estado">Estado</Label>
            <MultiSelect
              opciones={ESTADOS.map((e) => ({
                value: e,
                label: e.charAt(0).toUpperCase() + e.slice(1),
              }))}
              seleccionados={estadosFiltro}
              onChange={(v) => setEstadosFiltro(v as EstadoColada[])}
              placeholder="Todos"
              etiquetaResumen="estados"
              className="w-[160px]"
            />
          </div>

          <div className="grid gap-1.5">
            <Label>Planta</Label>
            <FiltroPlantaMulti idsPlanta={idsPlanta} onChange={setIdsPlanta} />
          </div>

          <div className="flex gap-2">
            <div className="grid min-w-0 flex-1 gap-1.5 sm:flex-none">
              <Label htmlFor="desde">Desde</Label>
              <Input
                id="desde"
                type="date"
                value={desde}
                onChange={(e) => {
                  setDesde(e.target.value);
                }}
                className="sm:w-[160px]"
              />
            </div>

            <div className="grid min-w-0 flex-1 gap-1.5 sm:flex-none">
              <Label htmlFor="hasta">Hasta</Label>
              <Input
                id="hasta"
                type="date"
                value={hasta}
                onChange={(e) => {
                  setHasta(e.target.value);
                }}
                className="sm:w-[160px]"
              />
            </div>
          </div>

          <div className="grid flex-1 gap-1.5 min-w-[200px]">
            <Label htmlFor="q">Buscar destino</Label>
            <div className="relative">
              <Search
                className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                id="q"
                placeholder="Cliente, obra (VB-6.24), sitio, receta…"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                }}
                className="pl-8"
              />
            </div>
          </div>

          <div className="grid gap-1.5 min-w-[180px]">
            <Label htmlFor="receta-blend-q">Receta Blend</Label>
            <div className="relative">
              <Search
                className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                id="receta-blend-q"
                placeholder="Muros, 210 losa, Sello..."
                value={recetaBlendQ}
                onChange={(e) => {
                  setRecetaBlendQ(e.target.value);
                }}
                className="pl-8"
              />
            </div>
          </div>

          {/* N° Pedido BC no le sirve a un OperadorFiori "puro" — no maneja
              envíos a BC, solo registra y revisa sus propias coladas. */}
          {!soloOperadorFiori && (
            <>
              <div className="grid gap-1.5">
                <Label htmlFor="pedido-bc-estado">N° Pedido BC</Label>
                <MultiSelect
                  opciones={[
                    { value: 'con', label: 'Con N°' },
                    { value: 'sin', label: 'Sin N°' },
                  ]}
                  seleccionados={pedidoBcEstados}
                  onChange={(v) => {
                    setPedidoBcEstados(v as PedidoBcEstadoFiltro[]);
                    // Si quedó filtrando SOLO por 'sin', limpiamos el input de búsqueda por texto.
                    if (v.length === 1 && v[0] === 'sin') setPedidoBcQ('');
                  }}
                  placeholder="Todos"
                  className="w-[140px]"
                />
              </div>

              {!(pedidoBcEstados.length === 1 && pedidoBcEstados[0] === 'sin') && (
                <div className="grid gap-1.5 min-w-[180px]">
                  <Label htmlFor="pedido-bc-q">Buscar N° Pedido</Label>
                  <div className="flex items-stretch overflow-hidden rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                    <span className="flex items-center bg-muted px-3 font-mono text-sm font-semibold text-muted-foreground">
                      PENS
                    </span>
                    <input
                      id="pedido-bc-q"
                      placeholder="2285"
                      value={
                        pedidoBcQ.toUpperCase().startsWith('PENS') ? pedidoBcQ.slice(4) : pedidoBcQ
                      }
                      onChange={(e) => {
                        const v = e.target.value;
                        setPedidoBcQ(v.trim() === '' ? '' : `PENS${v}`);
                      }}
                      className="flex-1 bg-transparent px-3 py-2 font-mono text-sm outline-none"
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Barra de selección. Visible solo cuando hay ≥1 colada seleccionada
          Y el usuario puede operar (las dos acciones acá son operativas). */}
      {seleccionadas.size > 0 && puedeOperar && (
        <Card className="border-adelante-primary/40 bg-adelante-primary/5">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 p-3">
            <div className="text-sm">
              <span className="font-semibold">{seleccionadas.size}</span>{' '}
              {seleccionadas.size === 1 ? 'colada seleccionada' : 'coladas seleccionadas'}
              {(() => {
                // Calculamos restricciones para mensajes contextuales.
                const seleccionadasArr = coladas.filter((c) => seleccionadas.has(c.id_colada));
                const todasSugeridas =
                  seleccionadasArr.length > 0 &&
                  seleccionadasArr.every((c) => c.estado === 'sugerida');
                if (seleccionadas.size === 1) {
                  return (
                    <span className="ml-2 text-muted-foreground">
                      (necesitás al menos 2 para consolidar)
                    </span>
                  );
                }
                if (!todasSugeridas) {
                  return (
                    <span className="ml-2 text-muted-foreground">
                      (consolidar requiere todas en sugerida)
                    </span>
                  );
                }
                return null;
              })()}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={consolidar.isPending || bulkObraEnCurso}
                onClick={() => setModalAsignarObraBulk(true)}
                className="gap-1.5"
              >
                <Building2 className="h-4 w-4" /> Asignar obra
              </Button>
              <Button
                size="sm"
                disabled={
                  seleccionadas.size < 2 ||
                  consolidar.isPending ||
                  bulkObraEnCurso ||
                  !coladas
                    .filter((c) => seleccionadas.has(c.id_colada))
                    .every((c) => c.estado === 'sugerida')
                }
                onClick={() => setModalConsolidar(true)}
                className="gap-1.5"
              >
                <Combine className="h-4 w-4" /> Consolidar
              </Button>
              {puedeAnular && (
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={
                    consolidar.isPending ||
                    bulkObraEnCurso ||
                    anularBulk.isPending ||
                    !coladas
                      .filter((c) => seleccionadas.has(c.id_colada))
                      .every((c) => ['sugerida', 'confirmada', 'digitada'].includes(c.estado))
                  }
                  onClick={() => setModalAnularBulk(true)}
                  className="gap-1.5"
                >
                  <Ban className="h-4 w-4" /> Anular
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSeleccionadas(new Set())}
                disabled={consolidar.isPending || bulkObraEnCurso || anularBulk.isPending}
              >
                <X className="h-4 w-4" /> Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]" />
                <TableHead className="w-[80px]">#</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Planta</TableHead>
                <TableHead>Receta Blend</TableHead>
                <TableHead>Receta BC</TableHead>
                <TableHead>Destino</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead className="text-right">m³</TableHead>
                <TableHead className="text-right">Batches</TableHead>
                <TableHead className="text-right">Alarmas</TableHead>
                <TableHead>
                  <button
                    type="button"
                    onClick={() => {
                      // Cicla: fecha_desc → pedido_bc_asc → pedido_bc_desc → fecha_desc
                      setOrden(
                        orden === 'fecha_desc'
                          ? 'pedido_bc_asc'
                          : orden === 'pedido_bc_asc'
                            ? 'pedido_bc_desc'
                            : 'fecha_desc',
                      );
                    }}
                    className="flex items-center gap-1 text-left font-medium hover:text-foreground"
                  >
                    N° Pedido BC
                    {orden === 'pedido_bc_asc' && <ArrowUp className="h-3 w-3" />}
                    {orden === 'pedido_bc_desc' && <ArrowDown className="h-3 w-3" />}
                    {orden === 'fecha_desc' && <ArrowUpDown className="h-3 w-3 opacity-30" />}
                  </button>
                </TableHead>
                <TableHead>Obra</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {consulta.isLoading &&
                SKELETON_FILAS.map((idFila) => (
                  <TableRow key={idFila}>
                    {SKELETON_COLUMNAS.map((idCol) => (
                      <TableCell key={`${idFila}-${idCol}`}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              {!consulta.isLoading && coladas.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={13}
                    className="py-10 text-center text-sm text-muted-foreground"
                  >
                    No hay coladas que coincidan con los filtros.
                  </TableCell>
                </TableRow>
              )}
              {!consulta.isLoading &&
                coladas.map((c) => {
                  // Las anuladas son las únicas inmutables. El resto (incluso
                  // cerradas) admite al menos asignar obra en bulk, que es la
                  // acción más permisiva. Consolidar se sigue restringiendo
                  // a sugeridas en el botón de Consolidar.
                  const seleccionable = c.estado !== 'anulada';
                  const seleccionada = seleccionadas.has(c.id_colada);
                  return (
                    <TableRow
                      key={c.id_colada}
                      onClick={(e) => {
                        // Click sobre el checkbox no debe navegar. Si el target
                        // es el input, lo dejamos pasar (su onChange ya maneja).
                        if ((e.target as HTMLElement).tagName === 'INPUT') return;
                        // Pasamos la URL actual (incluye los search params) en
                        // location.state para que el botón "Volver" del detalle
                        // pueda regresarnos preservando los filtros aplicados.
                        navegar(`/coladas/${c.id_colada}`, {
                          state: { from: `${location.pathname}${location.search}` },
                        });
                      }}
                      className={cn(
                        'cursor-pointer hover:bg-zinc-50',
                        seleccionada && 'bg-adelante-primary/5',
                      )}
                    >
                      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                        {seleccionable ? (
                          <input
                            type="checkbox"
                            checked={seleccionada}
                            onChange={(e) => {
                              setSeleccionadas((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(c.id_colada);
                                else next.delete(c.id_colada);
                                return next;
                              });
                            }}
                            disabled={consolidar.isPending}
                            className="h-4 w-4 cursor-pointer"
                            aria-label={`Seleccionar colada ${c.codigo_interno}`}
                          />
                        ) : null}
                      </TableCell>
                      <TableCell className="font-mono font-medium">{c.codigo_interno}</TableCell>
                      <TableCell>
                        <BadgeEstadoColada estado={c.estado} motivoAnulacion={c.motivo_anulacion} />
                      </TableCell>
                      <TableCell className="text-sm">
                        {nombrePlantaDisplay(c.planta_nombre)}
                      </TableCell>
                      <TableCell className="text-sm">{c.receta_blend_nombre}</TableCell>
                      <TableCell className="text-sm">
                        {c.codigo_receta_bc ?? (
                          <span className="italic text-muted-foreground">sin mapear</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className={cn(c.nombre_canonico ? '' : 'text-muted-foreground')}>
                          {c.destino_display}
                        </div>
                        {c.nombre_canonico && c.destino_raw !== c.nombre_canonico && (
                          <div className="text-xs text-muted-foreground">
                            (digitado: {c.destino_raw})
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm font-mono">
                        <FechaCR fecha={c.fecha_inicio} formato="corto" />
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums">
                        {c.m3_producidos.toFixed(3)}
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums">
                        {c.cantidad_batches}
                      </TableCell>
                      <TableCell
                        className={cn(
                          'text-right font-mono tabular-nums',
                          c.cantidad_alarmas_total > 0 && 'text-destructive font-semibold',
                        )}
                      >
                        {c.cantidad_alarmas_total}
                      </TableCell>
                      <TableCell className="text-sm font-mono">
                        {c.numero_pedido_ensamblado_bc ?? (
                          <span className="italic text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {c.obra_works_no ? (
                          <div title={c.obra_display_name ?? c.obra_works_no}>
                            <div className="font-mono text-xs">{c.obra_works_no}</div>
                            {c.obra_display_name && (
                              <div className="truncate text-xs text-muted-foreground max-w-[180px]">
                                {c.obra_display_name}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="italic text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>

          {/* Paginación */}
          {!consulta.isLoading && total > 0 && (
            <div className="flex items-center justify-between border-t px-4 py-3">
              <div className="text-sm text-muted-foreground">
                {coladas.length === 0
                  ? '0 resultados'
                  : `Mostrando ${(pagina - 1) * POR_PAGINA + 1}–${
                      (pagina - 1) * POR_PAGINA + coladas.length
                    } de ${total}`}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagina <= 1}
                  onClick={() => setPagina(Math.max(1, pagina - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Anterior
                </Button>
                <span className="text-sm tabular-nums">
                  {pagina} / {totalPaginas}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagina >= totalPaginas}
                  onClick={() => setPagina(Math.min(totalPaginas, pagina + 1))}
                >
                  Siguiente
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {consulta.error && (
        <p className="text-sm text-destructive">
          Error al cargar coladas: {consulta.error.message}
        </p>
      )}

      <ModalConsolidar
        abierto={modalConsolidar}
        cerrar={() => setModalConsolidar(false)}
        coladasSeleccionadas={coladas.filter((c) => seleccionadas.has(c.id_colada))}
        onConfirmar={() =>
          consolidar.mutate(
            { ids_a_consolidar: Array.from(seleccionadas) },
            {
              onSuccess: (respuesta) => {
                if (respuesta.data) {
                  // Éxito real: limpiamos selección y cerramos el modal.
                  setSeleccionadas(new Set());
                  setModalConsolidar(false);
                } else if (respuesta.error) {
                  // Respuesta 200 con error en body (poco común, pero posible).
                  // Cerramos el modal de confirm y abrimos el de error.
                  setModalConsolidar(false);
                  setErrorConsolidacion({
                    mensaje: respuesta.error.mensaje,
                    codigo: respuesta.error.codigo,
                  });
                }
              },
              onError: (e) => {
                // Cerramos el modal de confirm y abrimos un modal persistente
                // con el detalle. Para 409 ESTADO_INVALIDO el backend devuelve
                // un mensaje accionable que la operadora necesita leer entero.
                setModalConsolidar(false);
                if (e instanceof ApiError) {
                  setErrorConsolidacion({
                    mensaje: e.message,
                    codigo: e.codigo,
                    status: e.status,
                  });
                } else {
                  setErrorConsolidacion({ mensaje: e.message });
                }
              },
            },
          )
        }
        cargando={consolidar.isPending}
      />

      <ModalErrorConsolidacion
        error={errorConsolidacion}
        cerrar={() => setErrorConsolidacion(null)}
      />

      <ModalAsignarObraBulk
        abierto={modalAsignarObraBulk}
        cerrar={() => setModalAsignarObraBulk(false)}
        coladasSeleccionadas={coladas.filter((c) => seleccionadas.has(c.id_colada))}
        cargando={bulkObraEnCurso}
        onConfirmar={asignarObraEnBulk}
      />

      <ModalAnularBulk
        abierto={modalAnularBulk}
        cerrar={() => setModalAnularBulk(false)}
        coladasSeleccionadas={coladas.filter((c) => seleccionadas.has(c.id_colada))}
        cargando={anularBulk.isPending}
        onConfirmar={(motivo, eliminarBatches) =>
          anularBulk.mutate(
            {
              ids: Array.from(seleccionadas),
              motivo_anulacion: motivo,
              eliminar_batches: eliminarBatches,
            },
            {
              onSuccess: (respuesta) => {
                if (respuesta.data) {
                  setSeleccionadas(new Set());
                  setModalAnularBulk(false);
                }
              },
            },
          )
        }
      />
    </div>
  );
}

// =============================================================================
// Modal de consolidación
// =============================================================================

function ModalConsolidar(props: {
  abierto: boolean;
  cerrar: () => void;
  coladasSeleccionadas: ColadaListadoItem[];
  onConfirmar: () => void;
  cargando: boolean;
}) {
  // Principal = id_colada más bajo (mismo criterio que el backend, así la UI
  // muestra exactamente cuál va a sobrevivir).
  const ordenadas = [...props.coladasSeleccionadas].sort((a, b) => a.id_colada - b.id_colada);
  const principal = ordenadas[0];
  const absorbidas = ordenadas.slice(1);

  // Validaciones visuales antes de confirmar:
  const todasSugeridas = ordenadas.every((c) => c.estado === 'sugerida');
  const idsPlantas = new Set(ordenadas.map((c) => c.planta_nombre));
  const mismaPlanta = idsPlantas.size === 1;
  const valido = todasSugeridas && mismaPlanta && ordenadas.length >= 2;

  return (
    <Dialog open={props.abierto} onOpenChange={(o) => !o && props.cerrar()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Consolidar {ordenadas.length} coladas</DialogTitle>
          <DialogDescription>
            Todas las coladas absorbidas pasarán a <strong>anulada</strong> con motivo automático.
            Los batches se moverán a la colada principal (la de menor número). No se puede deshacer
            desde la UI.
          </DialogDescription>
        </DialogHeader>

        {!todasSugeridas && (
          <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">
            Solo se pueden consolidar coladas en estado <strong>sugerida</strong>. Quitá de la
            selección las que no lo estén.
          </div>
        )}
        {!mismaPlanta && (
          <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">
            Las coladas seleccionadas pertenecen a plantas distintas (
            {Array.from(idsPlantas).join(', ')}). Solo se pueden consolidar coladas de la{' '}
            <strong>misma planta</strong>.
          </div>
        )}

        {principal && (
          <div className="space-y-2 text-sm">
            <div>
              <strong>Principal (sobrevive):</strong>
              <div className="mt-1 rounded-md border border-emerald-300 bg-emerald-50 p-2 font-mono">
                #{principal.codigo_interno} — {principal.receta_blend_nombre} —{' '}
                {principal.destino_display}
              </div>
            </div>
            <div>
              <strong>
                Absorbidas (se anulan, {absorbidas.length}
                {absorbidas.length === 1 ? ' colada' : ' coladas'}):
              </strong>
              <ul className="mt-1 space-y-1">
                {absorbidas.map((c) => (
                  <li
                    key={c.id_colada}
                    className="rounded-md border border-zinc-200 bg-zinc-50 p-2 font-mono text-xs"
                  >
                    #{c.codigo_interno} — {c.receta_blend_nombre} — {c.destino_display} (
                    {c.cantidad_batches} {c.cantidad_batches === 1 ? 'batch' : 'batches'},{' '}
                    {c.m3_producidos.toFixed(3)} m³)
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={props.cerrar} disabled={props.cargando}>
            <X className="h-4 w-4" /> Cancelar
          </Button>
          <Button
            type="button"
            onClick={props.onConfirmar}
            disabled={!valido || props.cargando}
            className="gap-1.5"
          >
            <Combine className="h-4 w-4" />
            {props.cargando ? 'Consolidando…' : 'Consolidar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Modal de error de consolidación (persistente)
// =============================================================================
//
// Se abre cuando `consolidar.mutate` falla (típicamente 409 ESTADO_INVALIDO
// si alguna colada seleccionada ya no está en 'sugerida' — p. ej. la
// operadora la cerró desde otra pestaña). NO usamos toast porque el mensaje
// del backend trae el código y estado de cada colada problemática y la
// operadora necesita verlo con calma.
//
// El modal es bloqueante y solo se cierra con el botón "Entendido".

function ModalErrorConsolidacion(props: {
  error: { mensaje: string; codigo?: string; status?: number } | null;
  cerrar: () => void;
}) {
  const abierto = props.error !== null;
  const titulo = (() => {
    if (!props.error) return 'No se pudo consolidar';
    if (props.error.codigo === 'ESTADO_INVALIDO') {
      return 'No se pueden consolidar estas coladas';
    }
    if (props.error.codigo === 'PLANTAS_DISTINTAS') {
      return 'Coladas de plantas distintas';
    }
    return 'No se pudo consolidar';
  })();
  // Ayuda contextual según el código del backend. Para errores conocidos
  // damos un siguiente paso concreto, no solo el mensaje crudo.
  const ayuda = (() => {
    if (!props.error) return null;
    if (props.error.codigo === 'ESTADO_INVALIDO') {
      return (
        <>
          Solo se pueden consolidar coladas en estado <strong>sugerida</strong>. Si alguna ya está
          confirmada, digitada o cerrada, quitala de la selección y volvé a intentar. Si esto te
          sorprende, pedí a la pantalla un <em>refrescar</em>: alguien más pudo haberla cambiado.
        </>
      );
    }
    if (props.error.codigo === 'PLANTAS_DISTINTAS') {
      return (
        <>
          La consolidación solo aplica entre coladas de la <strong>misma planta</strong>. Filtrá por
          planta antes de seleccionar.
        </>
      );
    }
    return null;
  })();
  return (
    <Dialog open={abierto} onOpenChange={(o) => !o && props.cerrar()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <X className="h-5 w-5 rounded-full bg-red-100 p-0.5" />
            {titulo}
          </DialogTitle>
          <DialogDescription>
            El servidor rechazó la operación. <strong>Ninguna colada fue modificada.</strong>
          </DialogDescription>
        </DialogHeader>
        {props.error && (
          <div className="space-y-3 text-sm">
            <div className="rounded-md border border-red-300 bg-red-50 p-3 text-red-900">
              {props.error.mensaje}
            </div>
            {ayuda && <div className="text-zinc-700">{ayuda}</div>}
            {(props.error.codigo || props.error.status) && (
              <div className="font-mono text-xs text-zinc-500">
                {props.error.status ? `HTTP ${props.error.status}` : null}
                {props.error.status && props.error.codigo ? ' · ' : null}
                {props.error.codigo ?? null}
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button type="button" onClick={props.cerrar}>
            Entendido
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Modal de asignación de obra en bulk
// =============================================================================
//
// Aplica la misma obra (o la remueve) a todas las coladas seleccionadas en
// estados que aceptan asignar obra: sugerida, confirmada, digitada, cerrada.
// Las anuladas no entran porque `seleccionable` las filtra y el endpoint
// single las rechaza igual si llegaran.
//
// UX: mostramos un resumen de qué se va a hacer + el PickerObras. El padre
// hace el loop en serie y muestra un toast con el resumen final.

function ModalAsignarObraBulk(props: {
  abierto: boolean;
  cerrar: () => void;
  coladasSeleccionadas: ColadaListadoItem[];
  cargando: boolean;
  onConfirmar: (obraWorksNo: string | null) => void;
}) {
  const [seleccion, setSeleccion] = useState<string | null>(null);

  // Reset al abrir/cerrar para evitar arrastrar selección anterior.
  useMemo(() => {
    if (props.abierto) setSeleccion(null);
  }, [props.abierto]);

  const conteoPorEstado = props.coladasSeleccionadas.reduce<Record<string, number>>((acc, c) => {
    acc[c.estado] = (acc[c.estado] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <Dialog
      open={props.abierto}
      onOpenChange={(o) => {
        if (!o) props.cerrar();
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Asignar obra a {props.coladasSeleccionadas.length}{' '}
            {props.coladasSeleccionadas.length === 1 ? 'colada' : 'coladas'}
          </DialogTitle>
          <DialogDescription>
            La obra (works_no) se asocia a las coladas seleccionadas. Aplica a coladas en estado{' '}
            <strong>sugerida</strong>, <strong>confirmada</strong> o <strong>digitada</strong>. Si
            elegís "Sin obra" se remueve la obra actual de todas.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border bg-muted/40 p-3 text-sm">
          <div className="font-medium">Coladas seleccionadas por estado:</div>
          <ul className="mt-1 space-y-0.5 text-muted-foreground">
            {Object.entries(conteoPorEstado).map(([estado, n]) => (
              <li key={estado}>
                · {estado}: <span className="font-mono tabular-nums">{n}</span>
              </li>
            ))}
          </ul>
        </div>

        <PickerObras valor={seleccion} onChange={setSeleccion} maxAltoPx={220} />

        <DialogFooter>
          <Button type="button" variant="outline" onClick={props.cerrar} disabled={props.cargando}>
            <X className="h-4 w-4" /> Cancelar
          </Button>
          <Button
            type="button"
            onClick={() => props.onConfirmar(seleccion)}
            disabled={props.cargando}
            className="gap-1.5"
          >
            <Check className="h-4 w-4" />
            {props.cargando
              ? `Asignando… (${props.coladasSeleccionadas.length})`
              : seleccion
                ? 'Asignar a todas'
                : 'Quitar obra de todas'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Modal de anulación en lote
// =============================================================================

function ModalAnularBulk(props: {
  abierto: boolean;
  cerrar: () => void;
  coladasSeleccionadas: ColadaListadoItem[];
  cargando: boolean;
  onConfirmar: (motivo: string, eliminarBatches: boolean) => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [eliminarBatches, setEliminarBatches] = useState(true);

  // Reset al abrir para no arrastrar el motivo de un lote anterior.
  useMemo(() => {
    if (props.abierto) {
      setMotivo('');
      setEliminarBatches(true);
    }
  }, [props.abierto]);

  const ordenadas = [...props.coladasSeleccionadas].sort((a, b) => a.id_colada - b.id_colada);
  const hayDosificador = ordenadas.some((c) => c.origen_registro === 'dosificador');
  const valido = motivo.trim() !== '' && ordenadas.length > 0;

  return (
    <Dialog open={props.abierto} onOpenChange={(o) => !o && props.cerrar()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <Ban className="h-5 w-5" /> Anular {ordenadas.length}{' '}
            {ordenadas.length === 1 ? 'colada' : 'coladas'}
          </DialogTitle>
          <DialogDescription>
            Todas pasan a <strong>anulada</strong> con el mismo motivo. Esta acción no se puede
            deshacer desde la UI.
          </DialogDescription>
        </DialogHeader>

        <ul className="max-h-40 space-y-1 overflow-y-auto text-sm">
          {ordenadas.map((c) => (
            <li
              key={c.id_colada}
              className="rounded-md border border-zinc-200 bg-zinc-50 p-2 font-mono text-xs"
            >
              #{c.codigo_interno} — {c.receta_blend_nombre} — {c.destino_display} (
              {c.fecha_inicio.slice(0, 10)})
            </li>
          ))}
        </ul>

        <div className="grid gap-1.5">
          <Label htmlFor="motivo-anular-bulk">
            Motivo <span className="text-destructive">*</span>
          </Label>
          <Textarea
            id="motivo-anular-bulk"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ej: Fecha incorrecta por reloj de planta desconfigurado, se vuelve a cargar corregida."
            rows={3}
          />
        </div>

        {hayDosificador && (
          <label className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <input
              type="checkbox"
              checked={eliminarBatches}
              onChange={(e) => setEliminarBatches(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              <strong>Eliminar también los batches</strong> detrás de estas coladas. Necesario si
              vas a re-cargar un CSV corregido: la carga detecta duplicados por planta + N° de batch
              (no por fecha), así que si el batch viejo se queda en la base, la recarga lo va a
              saltar y la fecha corregida nunca va a entrar.
            </span>
          </label>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={props.cerrar} disabled={props.cargando}>
            <X className="h-4 w-4" /> Cancelar
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => props.onConfirmar(motivo.trim(), eliminarBatches)}
            disabled={!valido || props.cargando}
            className="gap-1.5"
          >
            <Ban className="h-4 w-4" />
            {props.cargando ? 'Anulando…' : 'Anular'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
